// DELIVERY-WI (WI-S1) — thin fetch functions mapping 1:1 to the
// endpoints in DeliveryController. The api.js axios instance unwraps
// the response payload, so each helper returns the DTO directly.
import api from '../services/api';

// WI-S3.1 (MAJOR-4) — single place that maps a rejected axios promise
// to a human-facing toast message. Every DeliveryPage / WorkItemDrawer
// catch calls this so 4xx / 5xx never lands silently. The server
// codes from WI-S1/S2/S3 are human by design ({code, message}); we
// surface the message and fall back to a status-shaped hint.
export const readServerError = (err) => {
  const data = err?.response?.data;
  if (data && typeof data === 'object') {
    if (data.message && typeof data.message === 'string') return data.message;
    if (data.code) return String(data.code);
  }
  if (typeof data === 'string' && data.length > 0) return data;
  const status = err?.response?.status;
  if (status === 401) return 'You are signed out. Please sign in again.';
  if (status === 403) return 'You are not allowed to do this here.';
  if (status === 409) return 'This item has moved on and can\'t be changed here.';
  if (status === 422) return 'Rejected — check the constraints and try again.';
  if (status >= 500) return 'Server error. Try again in a moment.';
  return err?.message || 'Something went wrong.';
};

// Convenience: `apiCall(fn, toast)` — runs the promise-returning `fn`
// and pipes any error to a toast. Returns the resolved value on
// success, or `null` on error so callers can guard with `if (r)`.
export const apiCall = async (fn, toast) => {
  try { return await fn(); }
  catch (err) {
    try { toast?.(readServerError(err), { variant: 'error' }); }
    catch { /* toast unavailable — ignore */ }
    return null;
  }
};

// ── Reader ────────────────────────────────────────────────────────────
export const getDelivery = (projectId) =>
  api.get(`/projects/${projectId}/delivery`);

// ── Owner (task) ──────────────────────────────────────────────────────
export const createWorkItem = (projectId, body) =>
  api.post(`/projects/${projectId}/work-items`, body);

// QLOG-S1 — resolve (or lazily create) the reusable non-system
// "Logged work" holder for the "Log hours" quick-log flow. Returns
// { workItemId }; caller then hits getWorkItemContext and opens the
// standard WorkItemDrawer. body: { projectTaskId, projectRoleId }.
export const quickLogAnchor = (projectId, body) =>
  api.post(`/projects/${projectId}/work-items/quick-log-anchor`, body);

export const patchWorkItem = (workItemId, body) =>
  api.patch(`/work-items/${workItemId}`, body);

export const deleteWorkItem = (workItemId) =>
  api.delete(`/work-items/${workItemId}`);

// ── Reader (take-over is the only reader-writer besides comments) ─────
export const takeOverWorkItem = (workItemId) =>
  api.post(`/work-items/${workItemId}/take-over`, {});

// ── Assignee / owner ──────────────────────────────────────────────────
export const startWorkItem = (workItemId) =>
  api.post(`/work-items/${workItemId}/start`, {});

export const closeWorkItem = (workItemId, explicitZero) =>
  api.post(`/work-items/${workItemId}/close`, { explicitZero: !!explicitZero });

// ── Reader ────────────────────────────────────────────────────────────
export const addComment = (workItemId, text) =>
  api.post(`/work-items/${workItemId}/comments`, { text });

// ── PM only ───────────────────────────────────────────────────────────
export const setTaskOwner = (projectId, taskId, ownerEmployeeId) =>
  api.patch(`/projects/${projectId}/tasks/${taskId}/owner`, { ownerEmployeeId });

// UDRAW-S2 §1.1 — self-service take-ownership. PM OR a member of the
// project team may call this to make themselves the task owner; the
// endpoint free-takes even when the task already has a different
// owner. Returns { taskId, ownerEmployeeId, ownerFullName }.
export const takeTaskOwnership = (projectId, taskId) =>
  api.post(`/projects/${projectId}/tasks/${taskId}/take-ownership`, {});

// UDRAW-S4 §1.2 — task-level discussion. Post an append-only comment
// on a ProjectTask. `text` is HTML (rich editor); the server enforces
// TEXT_REQUIRED / TEXT_TOO_LONG (2000 chars). Returns the created
// DeliveryCommentDto with authorFullName resolved.
export const addTaskComment = (projectId, taskId, text) =>
  api.post(`/projects/${projectId}/tasks/${taskId}/comments`, { text });

// UDRAW-S2 §1.2 — partial PATCH for delivery-side fields of a task.
// Body accepts { status?, notes? }; server keeps untouched fields.
// Rights: PM OR current task owner (403 otherwise). Errors: 400
// BAD_STATUS / NOTES_TOO_LONG. Returns { taskId, status, notes }.
export const updateTaskDeliveryMeta = (projectId, taskId, body) =>
  api.patch(`/projects/${projectId}/tasks/${taskId}/delivery-meta`, body || {});

// ASGN-S1 §2 — set/clear the task Assignee (изпълнител). Body:
// { assigneeEmployeeId: number | null }. Rights: PM OR current
// Owner (the service enforces; anyone else → 403). Response:
// { taskId, assigneeEmployeeId, assigneeFullName } so the drawer
// can flip its Assignee cell in place without a full re-GET.
export const setTaskAssignee = (projectId, taskId, assigneeEmployeeId) =>
  api.patch(`/projects/${projectId}/tasks/${taskId}/assignee`,
    { assigneeEmployeeId: assigneeEmployeeId ?? null });

// ── My Projects list signals (WI-S4 §2) ───────────────────────────────
// One row per project the caller is a team member of; four counts
// (myOpen, forTakeover, late, myHours) drive the new list columns.
export const getMyDeliverySignals = () => api.get('/my/delivery-signals');

// ── Assignee-only (WI-S2) ─────────────────────────────────────────────
// Batch upsert/delete hours on THIS WI. Each entry is (date, hours);
// hours=0 deletes the row for that day. Returns { ownHours,
// rollupHours, taskLoggedHours, monthTotals }.
// MT-S2 — entries may carry an optional `note` per day. Null preserves
// the existing server-side Note; "" clears it; a non-empty string
// upserts it (trimmed and truncated at 500 chars server-side).
export const logHours = (workItemId, entries) =>
  api.post(`/work-items/${workItemId}/hours`, { entries });

// MT-S2 — the WorkItemDrawer opened from My Timesheet needs the same
// props DeliveryPage feeds it (wi, task, team, caller). 404 =
// WI_NOT_FOUND; 403 = not a team member nor the assignee.
export const getWorkItemContext = (workItemId) =>
  api.get(`/work-items/${workItemId}/context`);

// ── Attachments (WI-S3) ───────────────────────────────────────────────
// Upload allowed for any reader; ALSO on Closed WIs (proof arrives
// after review). Delete requires uploader OR task owner OR PM AND the
// WI must not be Closed. Download URL is returned per-attachment so
// the drawer chip can point <a href>.
export const uploadAttachment = (workItemId, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/work-items/${workItemId}/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const attachmentDownloadUrl = (workItemId, attachmentId) =>
  api.getUri
    ? api.getUri({ url: `/work-items/${workItemId}/attachments/${attachmentId}` })
    : `/api/work-items/${workItemId}/attachments/${attachmentId}`;
export const deleteAttachment = (workItemId, attachmentId) =>
  api.delete(`/work-items/${workItemId}/attachments/${attachmentId}`);

// ── TC-S3 — read-only audit view of a work item for the calendar
// drill. Separate endpoint gated by Timesheet.ViewProjectReports (the
// caller is a PM browsing the calendar, not necessarily on the
// project team). Returns WorkItemAuditDto.
export const getWorkItemAudit = (workItemId) =>
  api.get(`/work-items/${workItemId}/audit`);
